import {
  CableCar,
  Camera,
  ExternalLink,
  Fish,
  Flame,
  Footprints,
  Gift,
  Landmark,
  Mountain,
  MoveDown,
  Snowflake,
  Sparkles,
  Ship,
  Ticket,
  Waves,
} from "lucide-react";
import { dictionaries } from "../lib/i18n";
import type { UiLang, Winter, WinterActivityKind } from "../lib/types";

interface Props {
  winter: Winter;
  lang: UiLang;
}

/**
 * Vinteren på stedet: hva som er stengt, og hva man gjør i stedet.
 *
 * ── Hvorfor rekkefølgen er som den er ──────────────────────────────────────
 *
 * Adkomsten står øverst, aktivitetene under. Det er ikke fordi det stengte er
 * viktigst, men fordi det er det som avgjør om resten er mulig: en liste over
 * hva man kan gjøre på Dalsnibba i februar er verdiløs hvis veien dit er
 * stengt, og en advarsel uten et alternativ er bare et nei.
 *
 * Begge må stå. Appen hadde bare den første halvparten — `winterWarning` per
 * stopp, vist som et notat — og resultatet var at en desemberplan besto av
 * hva som var stengt: fire notater om vinterstengte veier og snødekte stier,
 * og ingenting om at det er nordlys, hundekjøring og skrei på nettopp de
 * dagene. Det er ikke en feil i en enkelt setning; det er en app som beskriver
 * vinteren som et fravær.
 *
 * ── Hvorfor sesongen står på hver aktivitet ────────────────────────────────
 *
 * Rørosmartnan er fem dager i februar. Skreifisket er januar til april.
 * Hvalsafari nord for Tromsø er november til januar. Uten månedene ville
 * panelet lovet ting som ikke går den uka man er der, og det er den mest
 * skuffende formen for feil en reiseapp kan gjøre — man har reist.
 */

/** Ikon per type. Grupperer aktivitetene visuelt uten å måtte skrive kategorien. */
const KIND_ICON: Record<WinterActivityKind, typeof Snowflake> = {
  aurora: Sparkles,
  "ski-alpine": CableCar,
  "ski-nordic": Snowflake,
  "ski-touring": Mountain,
  dogsled: Footprints,
  reindeer: Footprints,
  snowshoe: Footprints,
  whale: Waves,
  fishing: Fish,
  sauna: Flame,
  sledding: MoveDown,
  market: Gift,
  museum: Landmark,
  "ice-climbing": Mountain,
  "fjord-cruise": Ship,
  snowmobile: Ticket,
  "guided-hike": Camera,
  skating: Snowflake,
  "cable-car": CableCar,
  glacier: Mountain,
};

export function WinterPanel({ winter, lang }: Props) {
  const t = dictionaries[lang];
  const { access, activities } = winter;

  return (
    <div className="winter">
      {access && (
        /*
         * Klassen følger status, ikke sesongen. Et sesongstengt sted er en
         * annen beskjed enn et krevende: det første er et nei, det andre er en
         * forberedelse. Å farge dem likt ville lært brukeren å overse begge.
         */
        <div className={`winter-access winter-access--${access.status}`}>
          <span className="winter-access-label">
            <Snowflake size={12} />
            {t.winterStatus[access.status]}
            {!access.openThisMonth && (
              <em className="winter-closed-flag">{t.winterNotInSeason}</em>
            )}
          </span>
          <p>{access.note}</p>
          <p className="winter-season">
            {t.winterSeason(access.openMonths.map((m) => t.monthShort[m - 1]).join(", "))}
          </p>
        </div>
      )}

      {activities.length > 0 && (
        <>
          <p className="winter-heading">{t.winterDoing(t.monthName[winter.month - 1])}</p>
          <ul className="winter-list">
            {activities.map((activity) => {
              const Icon = KIND_ICON[activity.kind] ?? Snowflake;
              return (
                <li key={activity.name} className="winter-item">
                  <span className="winter-kind" title={t.winterKind[activity.kind]}>
                    <Icon size={13} />
                  </span>
                  <div className="winter-body">
                    <div className="winter-name">
                      {activity.website ? (
                        <a href={activity.website} target="_blank" rel="noreferrer">
                          {activity.name} <ExternalLink size={10} />
                        </a>
                      ) : (
                        <strong>{activity.name}</strong>
                      )}
                      <span className="winter-tag">{t.winterKind[activity.kind]}</span>
                      {activity.fullDay && (
                        <span className="winter-tag winter-tag--day">{t.winterFullDay}</span>
                      )}
                    </div>
                    <p className="winter-note">{activity.note}</p>
                    <p className="winter-meta">
                      {t.winterMonths(
                        activity.months.map((m) => t.monthShort[m - 1]).join(", "),
                      )}
                      {/*
                        Bookingtiden er rådgivende og ikke ledig kapasitet —
                        ingen av aktørene har et åpent kapasitets-API. Samme
                        ærlighet som bookingtidene på de førte fotturene.
                      */}
                      {activity.bookAheadDays !== undefined && (
                        <> · {t.winterBookAhead(activity.bookAheadDays)}</>
                      )}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
